import {
  AuthorizationUser,
  AxiosError,
  CalculationPayload,
  CalculationResponse,
  Email,
} from '@price-for-profit/micro-services';
import { format, formatDistanceStrictWithOptions } from 'date-fns/fp';
import {
  CLIENT_ID,
  DEFAULT_QUOTE_PDF_FOOTER_DISPLAY,
  discountRecommendationBrands,
  EXTENDED_DESCRIPTION_DEFAULT,
  PERMISSIONS,
  RANKIN_QUOTE_PDF_FOOTER_DISPLAY,
} from 'shared/constants';
import md5 from 'blueimp-md5';
import {
  AppPermission,
  Brands,
  Expense,
  MarginParamsChild,
  ModelInput,
  ModelOutput,
  OrderNote,
  PayloadProduct,
  PdfExport,
  PdfForm,
  PdfInfo,
  Product,
  Quote,
  ResponseResult,
  TCustomProductForm,
  UserInfo,
  UtilityProduct,
} from 'shared/types';

export * from './build-csv-export';

const moneyFormatter = new Intl.NumberFormat('en', { style: 'currency', currency: 'USD' });
const moneyFormatterFourDecimals = new Intl.NumberFormat('en', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

const percentFormatter = new Intl.NumberFormat('en', {
  style: 'percent',
  minimumFractionDigits: 2,
  minimumIntegerDigits: 2,
});

export const formatMoney = (value: number) => moneyFormatter.format(value);
export const formatMoneyFourDecimals = (value: number) => moneyFormatterFourDecimals.format(value);

export const formatPercent = (value: number) => percentFormatter.format(value);

export const initialDateCompare = '1900-01-01';

export const formatDate = format('MM-dd-yy');
export const formatDateTime = format("M/dd/yyyy h:mmaaaaa'm'");

export const withExtension = (extension: string) => (value: string) => value.concat(extension);

export const addPdfExtension = withExtension('.pdf');
export const hashFileName = (phrase: string) => md5(phrase);

export const formatDateWithTime = format('MMddyyhhmm');

export const formatDateWithDistance = formatDistanceStrictWithOptions({ unit: 'day', roundingMethod: 'ceil' });

export const getAppPermissions = (InsightClaims: string) => {
  return InsightClaims.split(' ').filter(p => p.startsWith(CLIENT_ID)) as AppPermission[];
};

// Need to fix existing users: Change their current displayName to be firstName + lastName;
// The 'name' prop is the users displayName in Azure B2C.
export const getUserInfoFromIdClaims = ({
  given_name,
  family_name,
  name,
  email,
  sub,
  InsightClaims,
}: Record<string, string>): UserInfo => ({
  firstName: given_name,
  lastName: family_name,
  email,
  displayName: `${given_name} ${family_name}`, //name,
  id: sub,
  insightClaims: InsightClaims,
});

export const titleCase = (words: string) =>
  words
    ? words
        .trim()
        .split(' ')
        .map(([firstLetter, ...rest]) => `${firstLetter.toUpperCase()}${rest.join('').toLowerCase()}`)
        .join(' ')
    : '';

export const round = (value: number, places: number = 2) => (isNaN(value) ? 0 : Number(value.toFixed(places)));

export const marginRound = (value: number) => {
  if (isNaN(value)) return 0;

  return Number(value.toFixed(4));
};

export const getBrandsFromPermissions = (permissions: AppPermission[]) => {
  const permissionsToIgnore = [PERMISSIONS.login, PERMISSIONS.i2puser];
  return permissions.reduce<Brands[]>((list, p) => {
    if (!permissionsToIgnore.includes(p)) {
      list.push(p.split('.')[1] as Brands);
    }
    return list;
  }, []);
};

export const buildCostHelperText = (costEffectiveDate: string, brand: string) => {
  const isNewgen = brand === 'newgen';
  return `Use ${isNewgen ? 'replacement' : 'latest'} cost to ensure ${isNewgen ? 'accuracy' : 'accurate price'} ${
    costEffectiveDate && costEffectiveDate !== initialDateCompare
      ? `as of ${formatDate(new Date(costEffectiveDate))}`
      : ''
  }`;
};

export const buildListPriceHelperText = (listPriceEffectiveDate: string) =>
  `Use latest list price to ensure accuracy ${
    listPriceEffectiveDate && listPriceEffectiveDate !== initialDateCompare
      ? `as of ${formatDate(new Date(listPriceEffectiveDate))}`
      : ''
  }`;
export const costMarginToPrice = (cost: number, margin: number, decimals?: number) =>
  round(cost / (1 - margin), decimals);
export const costMarginToPriceAdjustedByFreightAdder = (cost: number, margin: number, freight: number) =>
  round(cost / (1 - margin)) * (1 + freight);
export const costDiscountToPrice = (cost: number, discount: number) => round(cost * (1 - discount));
export const costPriceToMargin = (cost: number, price: number) => round(Math.abs((cost - price) / price), 4);
export const costPriceToDiscount = (cost: number, price: number) => round(Math.abs((cost - price) / cost), 4);
export const generateQuoteNumber = (customerNumber: string, date: Date) => {
  return customerNumber + formatDateWithTime(date);
};

export function buildFieldNameWithIndex<T>(index: number) {
  return (field: keyof T) => {
    const productId = `products.${index}`;
    return `${productId}.${field}` as const;
  };
}

export function buildFieldExpenseNameWithIndex<T>(index: number) {
  return (field: keyof T) => {
    const expenseId = `expenses.${index}`;
    return `${expenseId}.${field}` as const;
  };
}

export function buildCustomProductFieldNameWithIndex<T>(index: number) {
  return (field: keyof T) => {
    const customProductId = `customProducts.${index}`;
    return `${customProductId}.${field}` as const;
  };
}

export function buildFieldOrderNoteNameWithIndex<T>(index: number) {
  return (field: keyof T) => {
    const orderNoteId = `orderNotes.${index}`;
    return `${orderNoteId}.${field}` as const;
  };
}

export const buildModelPayload = (
  customer: { customerNumber: string; customerType: string; competition: string; customerNumberAdjusted: string },
  products: PayloadProduct[],
  brand: Brands
): CalculationPayload<ModelInput> => {
  return {
    modelInputs: {
      customerNumber: customer.customerNumberAdjusted || '00000',
      competitiveFactor: customer.customerType,
      competition: customer.competition,
      childCustomer: customer.customerNumber || '00000',
      brand,
      marginParams: products.map(product => ({
        productGuid: product.productGuid,
        ItemID: product.itemId || 'NEW PRODUCT',
        VendorID: product.vendorId,
        ProductType: product.productType,
        ItemDescription: product.itemDescriptionAdj || 'NEWPRODUCT',
        Cost:
          brand === 'cbe' ? Number(product.lastCostPaid) * Number(product.exchangerate) : Number(product.lastCostPaid),
        ListMargin: product.marginAtList,
        ListPrice: product.listPrice || 0,
        PremiumHistoricalMargin: product.marginPercentileEighty,
        MROvsCapital: product?.MROvsCapital || 'NOT APPLICABLE',
        FactorSheetName: product.factorSheetName || 'NOT AVAILABLE',
      })),
    },
    includeInResponse: ['productGuid'],
  };
};

export const buildChildModelPayload = (
  customer: {
    customerNumber: string;
    customerNumberAdjusted: string;
    competitiveAdjustment: number;
    competitionAdjustment: number;
    customerType: string;
    customerTier: string;
  },
  product: PayloadProduct,
  brand: Brands,
  marginFloor: number
  // spxSurcharge: number
): CalculationPayload<MarginParamsChild> => {
  return {
    modelInputs: {
      productGuid: product.productGuid,
      CustomerNumber: customer.customerNumberAdjusted || '00000',
      CompetitiveAdjustment: customer.competitiveAdjustment,
      CustomerTier: customer.customerTier,
      CompetitionAdjustment: customer.competitionAdjustment,
      ItemID: product.itemId || 'NEW PRODUCT',
      ItemDescription: product.itemDescriptionAdj || 'NEW PRODUCT',
      ProductType: product.productType,
      Brand: brand,
      VendorID: product.vendorId,
      Cost:
        brand === 'cbe' ? Number(product.lastCostPaid) * Number(product.exchangerate) : Number(product.lastCostPaid),
      ListMargin: product.marginAtList,
      ListPrice: product.listPrice || 0,
      MarginFloor: marginFloor,
      ChildCustomer: customer.customerNumber || '00000',
      PremiumHistoricalMargin: product.marginPercentileEighty,
      competitiveFactor: customer.customerType,
      MROvsCapital: product.MROvsCapital || 'NOT APPLICABLE',
      FactorSheetName: product.factorSheetName || 'NOT AVAILABLE',
      // SPXSurcharge: spxSurcharge,
    },
    includeInResponse: ['productGuid'],
  };
};

export const buildQuoteProduct = (
  product: UtilityProduct,
  marginResult: ResponseResult,
  isDiscountBasedBrand: boolean,
  brand: Brands
): Product => {
  const isBrandThatUses4Digits: boolean = brand === 'nianticseal';
  const isCBEbrand: boolean = brand === 'cbe';
  const targetDiscount = marginRound(marginResult.TargetDiscount);
  const targetMargin = marginRound(marginResult.TargetMargin);
  const costAdjustedByExchangeRate = Number(product.lastCostPaid) * Number(product.exchangerate);
  const recommendedPrice = isDiscountBasedBrand
    ? costDiscountToPrice(Number(product.listPrice), targetDiscount)
    : isCBEbrand
    ? costMarginToPriceAdjustedByFreightAdder(costAdjustedByExchangeRate, targetMargin, Number(product.freightadder))
    : costMarginToPrice(Number(product.lastCostPaid), targetMargin, isBrandThatUses4Digits ? 4 : 2);
  const extendedPrice = recommendedPrice * Number(product.quantity);

  if (product.outOfScope || marginResult.IsCustomerVendorContracted) {
    return {
      ...product,
      salesRepAdjustedMargin: 0,
      lastMarginPaid: 0,
      maxMargin: 0,
      competitiveMargin: 0,
      revisedMargin: 0,
      targetMargin: 0,
      marginAtList: 0,
      marginPercentileEighty: 0,
      displayPremiumHistorical: false,
      lastMarginPaidDate: '',
      lastDiscountPaid: 0,
      revisedDiscount: 0,
      lastDiscountPaidDate: '',
      competitiveDiscount: 0,
      targetDiscount: 0,
      stretchDiscount: 0,
      currentCustomerItemDiscount: 0,
      premiumHistoricalDiscount: 0,
      extendedPrice: 0,
      recommendedPrice: 0,
      revisedPrice: 0,
      revisedReason: '',
      inventoryKicker: 'no',
      isCustomerVendorContracted: marginResult.IsCustomerVendorContracted,
      percentileBaseMargin: 0,
      adjustedBaseMargin: 0,
      increaseCap: 0,
      ttmMargin: 0,
      costDate: '',
      costEffectiveDate: '',
      listPriceEffectiveDate: '',
      MROvsCapital: product.MROvsCapital || 'NOT APPLICABLE',
      factorSheetName: product.factorSheetName || 'NOT AVAILABLE',
    };
  }

  return {
    ...product,
    extendedPrice,
    recommendedPrice,
    revisedPrice: recommendedPrice,
    targetMargin,
    revisedMargin: targetMargin,
    lastMarginPaid: marginRound(marginResult.LastMarginPaid),
    maxMargin: marginRound(marginResult.StretchMargin),
    competitiveMargin: marginRound(marginResult.CompetitiveMargin),
    displayPremiumHistorical: marginResult.DisplayPremiumHistoricalMarg,
    lastMarginPaidDate: marginResult.lastmarginpaiddate ? formatDate(new Date(marginResult.lastmarginpaiddate)) : '',
    salesRepAdjustedMargin: marginRound(marginResult.SalesRepAdjustedMargin),
    marginAtList: marginRound(marginResult.MarginAtList),
    marginPercentileEighty: marginRound(product.marginPercentileEighty),
    targetDiscount,
    revisedDiscount: targetDiscount,
    lastDiscountPaid: marginRound(marginResult.LastDiscountPaid),
    stretchDiscount: marginRound(marginResult.StretchDiscount),
    competitiveDiscount: marginRound(marginResult.CompetitiveDiscount),
    currentCustomerItemDiscount: marginRound(marginResult.CurrentCustomerItemDiscount),
    lastDiscountPaidDate: marginResult.lastmarginpaiddate ? formatDate(new Date(marginResult.lastmarginpaiddate)) : '',
    premiumHistoricalDiscount: marginRound(marginResult.PremiumHistoricalDiscount),
    revisedReason: '',
    inventoryKicker: 'no',
    isCustomerVendorContracted: marginResult.IsCustomerVendorContracted,
    recalculate: false,
    percentileBaseMargin: marginResult.PercentileBaseMargin,
    adjustedBaseMargin: marginResult.AdjustedBaseMargin,
    increaseCap: marginResult.IncreaseCap,
    ttmMargin: marginResult.TTMMargin,
    costDate: '',
    name: product.name.toUpperCase(),
    itemId: product.itemId.toUpperCase(),
    vendorName: product.vendorName.toUpperCase(),
    vendorId: product.vendorId.toUpperCase(),
    costEffectiveDate: product.costEffectiveDate ? formatDate(new Date(product.costEffectiveDate)) : '',
    listPriceEffectiveDate: product.listPriceEffectiveDate ? formatDate(new Date(product.listPriceEffectiveDate)) : '',
    MROvsCapital: product.MROvsCapital || 'NOT APPLICABLE',
    factorSheetName: product.factorSheetName || 'NOT AVAILABLE',
  };
};

export const buildQuoteExpense = (expense: Expense): Expense => {
  return {
    price: expense.price,
    type: expense.type,
    quantity: expense.quantity,
    extendedPrice: Number(expense.price) * Number(expense.quantity),
    itemized: expense.itemized,
  };
};

export const buildQuoteOrderNote = (orderNote: OrderNote): OrderNote => {
  return {
    note: orderNote.note,
    createdBy: orderNote.createdBy,
    creationDate: orderNote.creationDate,
  };
};

export const parseModelResult = (
  products: UtilityProduct[],
  calcResponse: CalculationResponse<ModelOutput>,
  isDiscountBasedBrand: boolean,
  brand: Brands,
  customProducts: TCustomProductForm[] = []
) => {
  const {
    data: { CallMarginModel, CustomerTier, CompetitionAdjustment, CompetitiveAdjustment, MarginFloor },
    metadata: { calculationId },
  } = calcResponse;

  const resultMap = CallMarginModel.reduce<Record<string, ResponseResult>>((map, result) => {
    map[result.productGuid] = result;
    return map;
  }, {});

  const productInfo = products.reduce<{
    total: number;
    products: Product[];
  }>(
    (map, product) => {
      product.calculationId = calculationId;
      const newProduct = buildQuoteProduct(product, resultMap[product.productGuid], isDiscountBasedBrand, brand);
      map.total += newProduct.extendedPrice;
      map.products.push(newProduct);
      return map;
    },
    { total: 0, products: [] }
  );

  const totalCustomProductsPrice = customProducts.reduce<number>(
    (sum, current) => sum + Number(current.customProductPrice) * Number(current.customProductQuantity),
    0
  );

  return {
    products: productInfo.products,
    quoteTotal: round(productInfo.total + totalCustomProductsPrice),
    customerTier: CustomerTier,
    competitionAdjustment: CompetitionAdjustment,
    competitiveAdjustment: CompetitiveAdjustment,
    marginFloor: MarginFloor,
    calculationId,
  } as const;
};

export const quoteToDefaultPdfForm = (quote: Quote): PdfForm => {
  return {
    quoteNumber: quote.quoteNumber,
    customerId: quote.customer.customerNumber,
    customerName: quote.customer.name,
    requestedBy: '',
    quoteDate: quote.submitDate,
    expirationDate: quote.expirationDate,
    quoteTotal: quote.quoteTotal,
    brand: quote.brand,
    rfqNumber: quote.pdfInfo.rfqNumber,
    billToName: quote.pdfInfo.billToName,
    billToAddressLine1: quote.pdfInfo.billToAddressLine1,
    billToAddressLine2: quote.pdfInfo.billToAddressLine2,
    billToPhone: quote.pdfInfo.billToPhone,
    shipToName: quote.pdfInfo.shipToName,
    shipToAddressLine1: quote.customer.customerShipTo?.shipToAddressLine1 || '',
    shipToAddressLine2: quote.customer.customerShipTo?.shipToAddressLine2 || '',
    sameAsShipTo: quote.pdfInfo.sameAsShipTo,
    productPrefix: quote.pdfInfo.productPrefix,
    delivery: quote.pdfInfo.delivery,
    quoteTerms: quote.pdfInfo.quoteTerms,
    paymentTerms: quote.pdfInfo.paymentTerms,
    freightTerms: quote.pdfInfo.freightTerms,
    modifiedQuoteNumber: quote.pdfInfo.modifiedQuoteNumber || quote.quoteNumber,
    quoteNotes: quote.pdfInfo.quoteNotes,
    quotedBy: quote.pdfInfo.quotedBy,
    quotedByEmail: quote.pdfInfo.quotedByEmail,
    quotedByPhoneNumber: quote.pdfInfo.quotedByPhoneNumber,
    salesRep: quote.pdfInfo.salesRep,
    salesRepEmail: quote.pdfInfo.salesRepEmail,
    salesRepPhoneNumber: quote.pdfInfo.salesRepPhoneNumber,
    products: quote.products
      ? quote.products.map((product, index) => ({
          lineNumber: index + 1,
          itemId: product.itemId || '',
          name: product.name || '',
          notes: product.lineItemNotes || '',
          quantity: product.quantity || 0,
          revisedPrice: product.revisedPrice || 0,
          extendedPrice: product.extendedPrice || 0,
          extendedDesc: product.extendedDesc && product.extendedDesc !== product.name ? product.extendedDesc : '',
          customerProductNumber: product.customerProductNumber || '',
        }))
      : [],
    customProducts: quote.customProducts
      ? quote.customProducts.map((customProduct, index) => ({
          ...customProduct,
          lineNumber: index + 1,
          notes: customProduct.lineItemNotes || '',
          customProductExtendedPrice:
            '$' + (Number(customProduct.customProductPrice) * Number(customProduct.customProductQuantity)).toFixed(2),
        }))
      : [],
    expenses: quote.expenses
      ? quote.expenses.map((expense, index) => ({
          lineNumber: index + 1,
          notes: '',
          type: expense.type,
          quantity: expense.quantity,
          extendedPrice: expense.extendedPrice,
          price: expense.price,
          itemized: true,
        }))
      : [],
    brandContact: quote.pdfInfo.brandContact,
    footerDisplayText: quote.brand === 'rankin' ? RANKIN_QUOTE_PDF_FOOTER_DISPLAY : DEFAULT_QUOTE_PDF_FOOTER_DISPLAY,
  };
};

export const updateQuoteFromPdfForm = (form: PdfForm, quote: Quote): Quote => {
  const pdfInfo: PdfInfo = {
    billToName: form.billToName,
    billToAddressLine1: form.billToAddressLine1,
    billToAddressLine2: form.billToAddressLine2,
    billToPhone: form.billToPhone,
    delivery: form.delivery,
    productPrefix: form.productPrefix,
    products: form.products.map(product => ({
      extendedDesc: product.extendedDesc,
      itemId: product.itemId,
      name: product.name,
      notes: product.notes,
    })),
    expenses: form.expenses.map(expense => ({
      type: expense.type,
      notes: expense.notes,
    })),
    quoteTerms: form.quoteTerms,
    paymentTerms: form.paymentTerms,
    freightTerms: form.freightTerms,
    modifiedQuoteNumber: form.modifiedQuoteNumber,
    quotedBy: form.quotedBy,
    quotedByEmail: form.quotedByEmail,
    quotedByPhoneNumber: form.quotedByPhoneNumber,
    salesRep: form.salesRep,
    salesRepEmail: form.salesRepEmail,
    salesRepPhoneNumber: form.salesRepPhoneNumber,
    quoteNotes: form.quoteNotes,
    requestedBy: form.requestedBy,
    rfqNumber: form.rfqNumber,
    sameAsShipTo: form.sameAsShipTo,
    shipToName: form.shipToName,
    shipToAddressLine1: form.shipToAddressLine1,
    shipToAddressLine2: form.shipToAddressLine2,
    brandContact: form.brandContact,
  };

  quote.pdfInfo = pdfInfo;

  return quote;
};

export const pdfFormToExport = (form: PdfForm): PdfExport => {
  const isDiscountBasedBrand: boolean = discountRecommendationBrands.includes(form.brand);
  const salesRep = titleCase(form.salesRep);
  const sameAsShipTo = form.sameAsShipTo === 'sameAsShipTo';
  const showShipTo = Boolean(form.shipToAddressLine1) || Boolean(form.shipToAddressLine2);
  const showBillTo = sameAsShipTo
    ? Boolean(form.shipToAddressLine1) || Boolean(form.billToAddressLine2)
    : Boolean(form.billToAddressLine1) || Boolean(form.billToAddressLine2) || Boolean(form.billToPhone);

  const showDelivery = Boolean(form.delivery);
  const showQuoteNotes = Boolean(form.quoteNotes);

  const formatByBrand = (price: number) => {
    if (form.brand === 'nianticseal') return formatMoneyFourDecimals(price);
    return formatMoney(price);
  };

  return {
    showBillTo,
    showShipTo,
    showDelivery,
    showQuoteNotes,
    quoteNumber: form.modifiedQuoteNumber,
    brand: form.brand,
    quoteDate: formatDate(new Date(form.quoteDate)),
    expirationDate: formatDate(new Date(form.expirationDate)),
    quoteTotal: formatByBrand(form.quoteTotal),
    customerId: form.customerId,
    customerName: form.customerName,
    requestedBy: form.requestedBy,
    billToName: sameAsShipTo ? form.shipToName : form.billToName,
    billToAddressLine1: sameAsShipTo ? form.shipToAddressLine1 : form.billToAddressLine1,
    billToAddressLine2: sameAsShipTo ? form.shipToAddressLine2 : form.billToAddressLine2,
    shipToName: form.shipToName,
    shipToAddressLine1: form.shipToAddressLine1,
    shipToAddressLine2: form.shipToAddressLine2,
    billToPhone: form.billToPhone,
    rfqNumber: form.rfqNumber,
    delivery: form.delivery,
    quoteTerms: form.quoteTerms,
    paymentTerms: form.paymentTerms,
    freightTerms: form.freightTerms,
    quoteNotes: form.quoteNotes,
    quotedBy: form.quotedBy,
    quotedByEmail: form.quotedByEmail,
    quotedByPhoneNumber: form.quotedByPhoneNumber,
    salesRep,
    salesRepEmail: form?.salesRepEmail?.toLowerCase(),
    salesRepPhoneNumber: form.salesRepPhoneNumber,
    products: form.products.map((product, i) => ({
      ...product,
      lineNumber: i + 1,
      itemId: `${form.productPrefix}${product.itemId}`,
      revisedPrice: formatByBrand(product.revisedPrice),
      extendedPrice: formatByBrand(product.extendedPrice),
      extendedDesc: product.extendedDesc === EXTENDED_DESCRIPTION_DEFAULT ? '' : product.extendedDesc,
    })),
    customProducts: form.customProducts.map((customProduct, index) => ({
      ...customProduct,
      lineNumber: index + 1,
      customProductPrice: formatByBrand(Number(customProduct.customProductPrice)),
      customProductExtendedPrice:
        '$' + (Number(customProduct.customProductPrice) * Number(customProduct.customProductQuantity)).toFixed(2),
    })),
    expenses: form.expenses.map((expense, i) => ({
      ...expense,
      extendedPrice: formatByBrand(expense.extendedPrice),
      price: formatByBrand(expense.price),
      quantity: expense.quantity,
      type: expense.type,
      lineNumber: i + 1,
      notes: expense.notes,
    })),
    footerDisplayText: form.brand === 'rankin' ? RANKIN_QUOTE_PDF_FOOTER_DISPLAY : DEFAULT_QUOTE_PDF_FOOTER_DISPLAY,
    ...form.brandContact,
    name: isDiscountBasedBrand ? (!!salesRep ? salesRep : form.quotedBy) : form.brandContact.name,
  };
};

export function buildEmail({
  subject,
  body,
  to,
  cc,
}: {
  subject: string;
  body: string;
  to: string[];
  cc: string[];
}): Email {
  return {
    actor: 'Demo Product Customer Quoting Drive Application',
    clientId: CLIENT_ID,
    subject,
    body,
    to,
    cc,
    bcc: [],
  };
}

export function getEmailTemplate(
  { brand, id, quoteNumber, salesRep, salesRepEmail, customer: { name } }: Quote,
  assignedSalesRepEmail: string
): Email {
  const appUrl = `${window.location.protocol}//${window.location.host}`;
  const url = `${appUrl}/quotes/${brand}/${id}`;

  return buildEmail({
    subject: `Quote ${quoteNumber} for ${name} has been submitted`,
    body: `
      Quote ${quoteNumber} for ${name} has been submitted. You can view this quote at the link below.

      Quote Link: ${url}

      Submitted By: ${salesRep}

      `,
    to: [assignedSalesRepEmail],
    cc: assignedSalesRepEmail.toLowerCase() === salesRepEmail.toLowerCase() ? [] : [salesRepEmail],
  });
}

export function getDemoPCQAuthClient(user: AuthorizationUser) {
  return user.clients.filter(client => client.clientId === CLIENT_ID)[0];
}

export function isAxiosError<T>(error: any): error is AxiosError<T> {
  return error.isAxiosError;
}
export const isPrePopulateTermsBrand = (brand: Brands) =>
  ['indelco', 'corrtech', 'qualitystainless', 'nianticseal', 'inscoprocess'].includes(brand);

export const buildExportBrandName = (brand: string, branch: string) => {
  return `${brand}${branch.toLowerCase() === 'qualitrol international' ? '-qualitrol' : ''}`;
};

export const copyToClipboard = async (text: string) => {
  if (!navigator.clipboard) {
    return;
  }
  try {
    await navigator.clipboard.writeText(text);
  } catch (e) {}
};
